import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import {
  DirectGasEmissionActivitiesList,
  PaginatedDirectGasEmissionActivitiesListList,
} from '@shared/api/types';
import AddDataMenu from '@app/components/imports/scope-1-2-import/components/add-data-menu';
import { baseTableSchema } from '@shared/components/data-table/schema';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useCCF } from '../../ccf-context';
import DirectGasEditForm from './direct-gas-edit-form';
import DirectGasPreviewSheet from './direct-gas-preview-sheet';
import { directGasTableColumns } from './direct-gas-table-columns';

export const schema = baseTableSchema.extend({
  uuid: z.string().optional(),
});

interface DirectGasDialogProps
  extends DataTableProps<PaginatedDirectGasEmissionActivitiesListList> {}
export default function DirectGasTable({
  data,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
  status,
}: DirectGasDialogProps) {
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const { scope3Supported } = useCCF();

  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState<DirectGasEmissionActivitiesList['uuid']>();

  const {
    showSimpleDialog: showDirectGasDialog,
    SimpleDialogComponent: DirectGasDialog,
    closeSimpleDialog: closeDirectGasDialog,
  } = useSimpleDialog();

  const { results: activities, count } = data || {};

  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { timePeriodsFilter, sitesFilter, gasesFilter, sourcesFilter } =
    useServerFilterOptions(
      ['timePeriods', 'sites', 'gases', 'sources'] as const,
      data?.filters || {}
    );

  const useFilterOptions: FilterOptions<
    ['timePeriods', 'sites', 'gases', 'sources']
  > = useMemo(
    () => ({
      timePeriods: timePeriodsFilter,
      sites: sitesFilter,
      gases: gasesFilter,
      sources: sourcesFilter,
    }),
    [timePeriodsFilter, sitesFilter, gasesFilter, sourcesFilter]
  );

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: directGasTableColumns({
      filters: useFilterOptions,
    }),
    pageCount,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        period: false,
      },
    },
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        className="bg-gray-00"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          setSelectedActivityUuid(row?.original.uuid);
          showDirectGasDialog();
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
      >
        <DataTableToolbar
          table={table}
          filterOrder={['site', 'source', 'gas', 'period']}
        >
          <DirectGasPreviewSheet />
          <AddDataMenu
            categorySlug="direct-gas-emissions"
            onAddManually={() => {
              setSelectedActivityUuid(undefined);
              showDirectGasDialog();
            }}
          />
        </DataTableToolbar>
      </DataTable>
      {DirectGasDialog({
        header: 'Add direct gas data',
        content: (
          <DirectGasEditForm
            activityUuid={selectedActivityUuid}
            closeDialog={closeDirectGasDialog}
          />
        ),
      })}
    </>
  );
}
